import React from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import {
  CalendarDays,
  Camera,
  Check,
  ChevronRight,
  Clapperboard,
  DollarSign,
  Gift,
  MapPin,
  Music2,
  Users,
} from 'lucide-react'
import { LuInstagram } from 'react-icons/lu'
import { cn } from '@/lib/utils'
import {
  DEAL_TYPES,
  deadlineLabel,
  maxBonus,
  opportunitiesPath,
  opportunityPath,
  opportunityPay,
  type Opportunity,
} from '@/lib/opportunities'
import { useGetOpportunitiesQuery } from '@/redux/collaborationsApi'

const BRAND_TONES = ['#2563EB', '#DC2626', '#7C3AED', '#0E7490', '#EA580C', '#15803D', '#DB2777']
/** A steady colour per brand, for its logo tile and the header when there's no photo */
const brandTone = (name?: string | null) =>
  BRAND_TONES[[...(name ?? '?')].reduce((n, c) => n + c.charCodeAt(0), 0) % BRAND_TONES.length]

const DEAL_ICON: Record<string, React.ElementType> = {
  gifting: Gift,
  appearance: MapPin,
  licensing: Clapperboard,
  content: Camera,
}

const BrandMark: React.FC<{ brand: Opportunity['brand'] }> = ({ brand }) => (
  <span
    className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl text-base font-bold text-[#FFFFFF] shadow-md ring-2 ring-[#FFFFFF]"
    style={{ background: brandTone(brand?.name) }}
  >
    {brand?.logo ? (
      <img src={brand.logo} alt="" className="h-full w-full object-cover" />
    ) : (
      (brand?.name ?? '?').slice(0, 2).toUpperCase()
    )}
  </span>
)

/** Small fact tile: an icon in a soft circle and a two-line label */
const Tile: React.FC<{
  icon: React.ElementType
  tint: string
  ink: string
  top: string
  sub?: string | null
}> = ({ icon: Icon, tint, ink, top, sub }) => (
  <div className="flex min-w-0 flex-col gap-1.5 rounded-xl bg-muted/60 p-2.5">
    <span
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
      style={{ background: tint }}
    >
      <Icon className="h-4 w-4" style={{ color: ink }} />
    </span>
    <span className="min-w-0">
      <span className="line-clamp-2 block text-xs font-semibold leading-tight text-foreground">
        {top}
      </span>
      {sub ? (
        <span className="mt-0.5 line-clamp-1 block text-xs leading-tight text-muted-foreground">
          {sub}
        </span>
      ) : null}
    </span>
  </div>
)

const platformName = (p: string) =>
  p.toLowerCase() === 'tiktok' ? 'TikTok' : p.toLowerCase() === 'youtube' ? 'YouTube' : 'Instagram'

const OpportunityCard: React.FC<{ item: Opportunity }> = ({ item }) => {
  const navigate = useNavigate()
  const due = deadlineLabel(item.deadline)
  const Icon = DEAL_ICON[item.dealType] ?? Gift
  const tone = brandTone(item.brand?.name)
  const bonus = maxBonus(item)
  const platforms = [...new Set(item.platforms.map(platformName))]

  const what =
    item.dealType === 'gifting'
      ? {
          top: item.product?.name ?? 'Free product',
          sub: item.product?.description ?? 'Sent to you',
        }
      : item.dealType === 'appearance'
        ? { top: 'In-person appearance', sub: 'Event, signing or clinic' }
        : item.dealType === 'licensing'
          ? { top: 'Content for their ads', sub: 'They license it' }
          : { top: 'Branded content', sub: 'Your concept' }
  const pay =
    item.dealType === 'gifting'
      ? bonus
        ? { top: `Up to $${bonus.toLocaleString()} bonus`, sub: 'As views come in' }
        : { top: 'Free product', sub: null }
      : item.payout
        ? {
            top: `$${Math.round(item.payout).toLocaleString()}`,
            sub: item.dealType === 'licensing' ? 'a month' : 'paid to you',
          }
        : { top: 'Free product', sub: null }
  const where =
    item.dealType === 'appearance'
      ? {
          icon: CalendarDays,
          top: item.endDate ? 'Dates set by the brand' : 'Date to agree',
          sub: null,
        }
      : platforms.length
        ? {
            icon: platforms[0] === 'TikTok' ? Music2 : LuInstagram,
            top: platforms.join(' or '),
            sub: 'Where you post',
          }
        : { icon: Users, top: 'Your audience', sub: null }

  return (
    <li>
      <button
        type="button"
        onClick={() => navigate(opportunityPath(item.id))}
        className="block w-full overflow-hidden rounded-2xl border border-border bg-card text-left shadow-sm transition hover:shadow-md"
      >
        {/* The brand's photo, or its colour when there isn't one */}
        <div
          className="relative h-36 w-full overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${tone} 0%, ${tone}CC 55%, #0F172A 140%)`,
          }}
        >
          {item.imageUrl ? (
            <img
              src={item.imageUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <Icon
              className="absolute -bottom-4 -right-3 h-32 w-32 text-[#FFFFFF]/15"
              strokeWidth={1.25}
            />
          )}
          <span className="absolute left-3 top-3">
            <BrandMark brand={item.brand} />
          </span>
          <span
            className={cn(
              'absolute right-3 top-3 rounded-full px-3 py-1 text-sm font-bold tabular-nums shadow',
              item.fit >= 70 ? 'bg-[#C0FD72] text-[#0F172A]' : 'bg-[#FFFFFF] text-[#0F172A]',
            )}
          >
            {item.fit}% fit
          </span>
        </div>

        <div className="space-y-3 p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="line-clamp-2 text-lg font-bold leading-snug text-foreground">
                {item.title}
              </p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {item.brand?.name ?? 'Brand'} · {item.dealTypeLabel} · {opportunityPay(item)}
              </p>
            </div>
            <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-muted-foreground" />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <Tile icon={Icon} tint="#FFEDD5" ink="#C2410C" top={what.top} sub={what.sub} />
            <Tile icon={DollarSign} tint="#FEF3C7" ink="#B45309" top={pay.top} sub={pay.sub} />
            <Tile icon={where.icon} tint="#FCE7F3" ink="#BE185D" top={where.top} sub={where.sub} />
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-border pt-3 text-sm">
            {item.applied ? (
              <span className="inline-flex items-center gap-1.5 font-semibold text-[#3F7A0A]">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#4D7C0F] text-[#FFFFFF]">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                Applied
              </span>
            ) : null}
            {item.fitReasons.length ? (
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                <Users className="h-4 w-4" />
                Matches {item.fitReasons.join(', ').toLowerCase()}
              </span>
            ) : null}
            {due ? (
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                <CalendarDays className="h-4 w-4" />
                {due}
              </span>
            ) : null}
          </div>
        </div>
      </button>
    </li>
  )
}

/**
 * The Marketplace: every opportunity a brand has opened to applications, best fit first,
 * filtered by deal type. Tapping one opens the apply screen.
 */
const Opportunities: React.FC = () => {
  const { type } = useParams<{ type: string }>()
  const navigate = useNavigate()
  const current = DEAL_TYPES.find((d) => d.id === type)
  const { data: all = [], isLoading, isError } = useGetOpportunitiesQuery()
  if (!current) return <Navigate to={opportunitiesPath('all')} replace />

  // Every opportunity open to the athlete, best fit first (the server ranks them)
  const items = current.dealType ? all.filter((o) => o.dealType === current.dealType) : all
  const counts = Object.fromEntries(
    DEAL_TYPES.map((d) => [
      d.id,
      d.dealType ? all.filter((o) => o.dealType === d.dealType).length : all.length,
    ]),
  )

  return (
    <div className="mx-auto w-full max-w-md pb-8">
      <h1 className="text-2xl font-bold text-foreground">{current.title}</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        {current.blurb}
      </p>

      <nav className="mt-4 flex flex-wrap gap-1.5" aria-label="Deal types">
        {DEAL_TYPES.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => navigate(opportunitiesPath(d.id))}
            aria-current={d.id === current.id ? 'page' : undefined}
            className={cn(
              'rounded-full border px-2.5 py-1.5 text-sm font-semibold',
              d.id === current.id
                ? 'border-[#C0FD72] bg-[#C0FD72] text-[#0F172A]'
                : 'border-border bg-card text-foreground hover:bg-muted',
            )}
          >
            {d.label}
            {counts[d.id] ? (
              <span className="ml-1 text-xs opacity-70 tabular-nums">{counts[d.id]}</span>
            ) : null}
          </button>
        ))}
      </nav>

      <div className="mt-5">
        {isLoading ? (
          <p className="text-sm text-gray-500">Finding your matches…</p>
        ) : isError ? (
          <p className="rounded-2xl border border-dashed border-foreground/15 p-4 text-sm text-gray-600">
            Couldn't load opportunities. Pull to refresh or try again in a moment.
          </p>
        ) : items.length ? (
          <ul className="space-y-4">
            {items.map((o) => (
              <OpportunityCard key={o.id} item={o} />
            ))}
          </ul>
        ) : (
          <div className="rounded-2xl border border-dashed border-foreground/15 bg-card p-6 text-center shadow-[0_1px_2px_rgba(30,41,59,0.04)]">
            <p className="font-bold text-foreground">
              {current.dealType
                ? `No ${current.label.toLowerCase()} deals right now`
                : 'Nothing open right now'}
            </p>
            <p className="mt-1 text-sm text-gray-600">
              New opportunities show up here as brands post them. Connected socials and a complete
              profile raise your fit.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default Opportunities
